import React from "react";
import {KTMScreen} from "ktm-ui";
import toastr from "toastr";
import {Button, Modal} from "react-bootstrap";
import moment from "moment";
import {Search, RotateCcw, TriangleAlert, CircleCheck, LoaderCircle} from "lucide-react";
import "../styles/UnfinalizeClaim.css";

const formatMoney = (value) =>
    "$" + Number(value || 0).toLocaleString("en-US", {minimumFractionDigits: 2, maximumFractionDigits: 2});

const statusTone = (status) => {
    if (status === "ADJUDICATED") return "info";
    if (status === "PENDING" || status === "PREADJUDICATED") return "warn";
    if (status === "PAID" || status === "ZEROPAID") return "ok";
    return "neutral";
};

const formatYear = (c) => {
    if (!c || !c.refundYearStart || !c.refundYearEnd) {
        return null;
    }
    return moment(c.refundYearStart).format("YYYY MMM DD") + " – " + moment(c.refundYearEnd).format("YYYY MMM DD");
};

const yearPhrase = (c) => {
    const range = formatYear(c);
    const which = c.isPreviousYearRefund ? "previous" : "current";
    return range ? `${which} benefit year (${range})` : `${which} benefit year`;
};

const initialResultState = {
    claimResult: null,
    blockReason: null,
    notFoundFor: null,
    reopened: null,
};

export default KTMScreen(
    {name: "unfinalizeclaim"},
    class extends React.Component<any, any> {
        state = {
            claimNo: "",
            searching: false,
            confirmOpen: false,
            submitting: false,
            ...initialResultState,
        }

        setInput = (e) => {
            this.setState({claimNo: e.target.value, ...initialResultState});
        };

        handleKeyDown = (e) => {
            if (e.key === "Enter") {
                this.submitSearch();
            }
        };

        resetSearch = () => {
            this.setState({claimNo: "", ...initialResultState});
        };

        submitSearch = () => {
            const claimNo = (this.state.claimNo || "").trim();
            if (claimNo === "" || this.state.searching) {
                return;
            }
            this.setState({searching: true, ...initialResultState});
            fetch("/api/claims/searchclaims/" + encodeURIComponent(claimNo), {method: "GET"})
                .then(async (response) => {
                    if (response.status === 404) {
                        this.setState({searching: false, notFoundFor: claimNo});
                        return;
                    }
                    if (!response.ok) {
                        throw new Error(String(response.status));
                    }
                    const data = await response.json();
                    this.setState({searching: false, claimResult: data.claim, blockReason: data.blockReason});
                })
                .catch(() => {
                    this.setState({searching: false});
                    toastr.error("Search failed. Please try again.", "Error");
                });
        };

        openConfirm = () => {
            this.setState({confirmOpen: true});
        };

        closeConfirm = () => {
            if (!this.state.submitting) {
                this.setState({confirmOpen: false});
            }
        };

        unfinalizeClaim = () => {
            if (this.state.submitting) {
                return;
            }
            this.setState({submitting: true});
            fetch("/api/claims/unfinalizeclaim", {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({claimNo: this.state.claimResult.claimNo}),
            })
                .then(async (response) => {
                    const data = await response.json().catch(() => ({}));
                    if (!response.ok || !data.success) {
                        this.setState({submitting: false, confirmOpen: false});
                        toastr.error(data.message || "Unfinalize failed. Please try again.", "Error");
                        return;
                    }
                    this.setState({
                        submitting: false,
                        confirmOpen: false,
                        reopened: {amount: data.amount, accountType: data.accountType},
                        claimResult: {...this.state.claimResult, status: "PENDING"},
                    });
                })
                .catch(() => {
                    this.setState({submitting: false, confirmOpen: false});
                    toastr.error("Unfinalize failed. Please try again.", "Error");
                });
        };

        renderFooter() {
            const {claimResult, blockReason, reopened} = this.state;
            if (reopened) {
                return (
                    <div className="ktm-unfin-banner ktm-unfin-banner--ok">
                        <div className="ktm-unfin-banner-body">
                            <CircleCheck size={18}/>
                            <span>
                                Claim reopened. It's now PENDING and {formatMoney(reopened.amount)} was returned to
                                {" "}{claimResult.employeeName || "the employee"}'s {reopened.accountType} balance in the {yearPhrase(claimResult)}.
                            </span>
                        </div>
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={this.resetSearch}>
                            Search another claim
                        </button>
                    </div>
                );
            }
            if (blockReason) {
                return (
                    <div className="ktm-unfin-banner ktm-unfin-banner--warn">
                        <TriangleAlert size={18}/>
                        <span>{blockReason}</span>
                    </div>
                );
            }
            return (
                <button type="button" className="btn btn-outline-danger" onClick={this.openConfirm}>
                    <RotateCcw size={16} style={{marginRight: 6, verticalAlign: -2}}/>
                    Unfinalize claim
                </button>
            );
        }

        renderResult() {
            const c = this.state.claimResult;
            if (!c) {
                return null;
            }
            return (
                <div className="ktm-unfin-card">
                    <div className="ktm-unfin-card-head">
                        <span className="ktm-unfin-claimno">{c.claimNo}</span>
                        <span className={"ktm-unfin-pill ktm-unfin-pill--" + statusTone(c.status)}>{c.status}</span>
                    </div>
                    <dl className="ktm-unfin-grid">
                        <div>
                            <dt>Employee</dt>
                            <dd>
                                {c.employeeName || "—"}
                                {c.employeeNo && <div className="ktm-unfin-sub">#{c.employeeNo}</div>}
                            </dd>
                        </div>
                        <div>
                            <dt>Policy</dt>
                            <dd>
                                {c.policyName || "—"}
                                {c.policyNo && <div className="ktm-unfin-sub">{c.policyNo}</div>}
                            </dd>
                        </div>
                        <div>
                            <dt>Account</dt>
                            <dd>{c.accountType || "—"}</dd>
                        </div>
                        <div>
                            <dt>Total approved</dt>
                            <dd className="ktm-unfin-amount">{formatMoney(c.totalApproved)}</dd>
                        </div>
                        <div>
                            <dt>Adjudicated</dt>
                            <dd>
                                {c.adjudicatedAt ? moment(c.adjudicatedAt).local().format("YYYY MMM DD") : "—"}
                                {c.adjudicatorName && <div className="ktm-unfin-sub">by {c.adjudicatorName}</div>}
                            </dd>
                        </div>
                        <div>
                            <dt>Payment</dt>
                            <dd>{c.isPaid ? "Included in a payment run" : "Not paid yet"}</dd>
                        </div>
                        <div>
                            <dt>Refund to</dt>
                            <dd>
                                {c.isPreviousYearRefund ? "Previous benefit year" : "Current benefit year"}
                                {formatYear(c) && <div className="ktm-unfin-sub">{formatYear(c)}</div>}
                            </dd>
                        </div>
                    </dl>
                    <div className="ktm-unfin-card-foot">
                        {this.renderFooter()}
                    </div>
                </div>
            );
        }

        renderConfirmModal() {
            const c = this.state.claimResult;
            if (!c) {
                return null;
            }
            return (
                <Modal show={this.state.confirmOpen} onHide={this.closeConfirm} centered>
                    <Modal.Header closeButton={!this.state.submitting}>
                        <Modal.Title>Unfinalize {c.claimNo}?</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>
                            This sets the claim back to <strong>PENDING</strong> and returns
                            {" "}<strong>{formatMoney(c.totalApproved)}</strong> to
                            {" "}<strong>{c.employeeName || "the employee"}</strong>'s {c.accountType} balance in the
                            {" "}<strong>{yearPhrase(c)}</strong>.
                        </p>
                        <p className="mb-0 text-muted">The claim will need to be adjudicated again before it can be paid.</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={this.closeConfirm} disabled={this.state.submitting}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={this.unfinalizeClaim} disabled={this.state.submitting}>
                            {this.state.submitting && <LoaderCircle size={16} className="ktm-unfin-spin"/>}
                            {this.state.submitting ? "Unfinalizing…" : "Unfinalize claim"}
                        </Button>
                    </Modal.Footer>
                </Modal>
            );
        }

        render() {
            const canSearch = (this.state.claimNo || "").trim() !== "" && !this.state.searching;
            return (
                <div className="ktm-financials-content ktm-unfin">
                    <div className="ktm-unfin-header">
                        <h2 className="ktm-unfin-title">Unfinalize Claim</h2>
                        <p className="ktm-unfin-subtitle">
                            Reopen an adjudicated claim that hasn't been paid yet. The approved amount is returned to the employee's balance.
                        </p>
                    </div>

                    <div className="ktm-unfin-search">
                        <div className="ktm-unfin-search-field">
                            <Search size={16}/>
                            <input
                                type="text"
                                className="form-control"
                                name="searchInput"
                                placeholder="Claim number, e.g. CA20449669"
                                value={this.state.claimNo}
                                onChange={this.setInput}
                                onKeyDown={this.handleKeyDown}
                                autoFocus
                            />
                        </div>
                        <button type="button" className="btn btn-primary" onClick={this.submitSearch} disabled={!canSearch}>
                            {this.state.searching && <LoaderCircle size={16} className="ktm-unfin-spin"/>}
                            {this.state.searching ? "Searching…" : "Search"}
                        </button>
                    </div>

                    <div className={"ktm-unfin-hint" + (this.state.notFoundFor ? " ktm-unfin-hint--error" : "")}>
                        {this.state.notFoundFor ? `No claim found for ${this.state.notFoundFor}.` : ""}
                    </div>

                    {this.renderResult()}
                    {this.renderConfirmModal()}
                </div>
            );
        }
    }
)
